import React, { useState, useRef } from 'react';
import { X, Upload, Download, CircleAlert, CircleCheck } from 'lucide-react';
import type { TBAccount, Engagement, Entity } from '../types';
import { db } from '../db/database';
import { dollarsToCents } from '../utils/format';
import { DEFAULT_GROUPING, DEFAULT_SUBGROUPING } from '../utils/mappings';

interface ImportTBModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  engagement: Engagement;
  entities: Entity[];
}

interface ParsedRow {
  accountNumber: string;
  accountName: string;
  bookBalance: number; // Already in cents
}

interface ConflictAccount {
  accountNumber: string;
  accountName: string;
  existingBalance: number;
  newBalance: number;
}

type ImportStatus = 'idle' | 'parsing' | 'validating' | 'conflicts' | 'importing' | 'success' | 'error';

export default function ImportTBModal({
  isOpen,
  onClose,
  onSave,
  engagement,
  entities
}: ImportTBModalProps) {
  const [selectedEntity, setSelectedEntity] = useState(entities[0]?.name ?? engagement.clientName);
  const [includeZeroBalance, setIncludeZeroBalance] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [conflicts, setConflicts] = useState<ConflictAccount[]>([]);
  const [conflictResolution, setConflictResolution] = useState<'replace' | 'cancel' | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setErrorMessage('');
      setSuccessMessage('');
      setParsedData([]);
      setConflicts([]);
      setConflictResolution(null);
      setIncludeZeroBalance(false);
    }
  }, [isOpen]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    setStatus('parsing');
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const fileName = file.name.toLowerCase();
      let data: ParsedRow[];

      if (fileName.endsWith('.csv')) {
        data = await parseCSV(file);
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        data = await parseXLSX(file);
      } else {
        throw new Error('Invalid file format. Please upload a CSV or Excel file.');
      }

      const filteredData = includeZeroBalance 
        ? data 
        : data.filter(row => row.bookBalance !== 0);

      if (filteredData.length === 0) {
        throw new Error('No valid accounts to import. All accounts have zero balance.');
      }

      setParsedData(filteredData);
      await validateAndCheckConflicts(filteredData);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred while processing the file.');
      console.error('File upload error:', error);
    }
  };

  const parseCSV = async (file: File): Promise<ParsedRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            reject(new Error('File must contain at least one data row.'));
            return;
          }

          const dataLines = lines.slice(1);
          const parsed: ParsedRow[] = [];
          const accountNumbers = new Set<string>();

          for (let i = 0; i < dataLines.length; i++) {
            const line = dataLines[i];
            const columns = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
            const cleanColumns = columns.map(col => col.replace(/^"|"$/g, '').trim());

            if (cleanColumns.length < 3) {
              reject(new Error(`Row ${i + 2}: Must have at least 3 columns (Account #, Name, Balance).`));
              return;
            }

            const accountNumber = cleanColumns[0];
            const accountName = cleanColumns[1];
            const balanceStr = cleanColumns[2];

            if (!accountNumber) {
              reject(new Error(`Row ${i + 2}: Account Number is required.`));
              return;
            }

            if (accountNumbers.has(accountNumber)) {
              reject(new Error(`Duplicate Account Number "${accountNumber}" found at row ${i + 2}.`));
              return;
            }
            accountNumbers.add(accountNumber);

            const balanceNum = parseFloat(balanceStr);
            if (isNaN(balanceNum)) {
              reject(new Error(`Row ${i + 2}: Book Balance "${balanceStr}" is not a valid number.`));
              return;
            }

            const bookBalanceCents = dollarsToCents(balanceNum);

            parsed.push({
              accountNumber,
              accountName: accountName || accountNumber, 
              bookBalance: bookBalanceCents
            });
          }

          resolve(parsed);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsText(file);
    });
  };

  const parseXLSX = async (file: File): Promise<ParsedRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const XLSX = await import('xlsx');
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1,
            raw: false 
          }) as (string | number | null)[][];

          if (jsonData.length < 2) {
            reject(new Error('File must contain at least one data row.'));
            return;
          }

          const dataRows = jsonData.slice(1);
          const parsed: ParsedRow[] = [];
          const accountNumbers = new Set<string>();

          for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];
            
            if (!row || row.length < 3) {
              reject(new Error(`Row ${i + 2}: Must have at least 3 columns (Account #, Name, Balance).`));
              return;
            }

            const accountNumber = String(row[0] || '').trim();
            const accountName = String(row[1] || '').trim();
            const balanceStr = String(row[2] || '').trim();

            if (!accountNumber) {
              reject(new Error(`Row ${i + 2}: Account Number is required.`));
              return;
            }

            if (accountNumbers.has(accountNumber)) {
              reject(new Error(`Duplicate Account Number "${accountNumber}" found at row ${i + 2}.`));
              return;
            }
            accountNumbers.add(accountNumber);

            const balanceNum = parseFloat(balanceStr);
            if (isNaN(balanceNum)) {
              reject(new Error(`Row ${i + 2}: Book Balance "${balanceStr}" is not a valid number.`));
              return;
            }

            const bookBalanceCents = dollarsToCents(balanceNum);

            parsed.push({
              accountNumber,
              accountName: accountName || accountNumber,
              bookBalance: bookBalanceCents
            });
          }

          resolve(parsed);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsArrayBuffer(file);
    });
  };

  const validateAndCheckConflicts = async (data: ParsedRow[]) => {
    setStatus('validating');

    try {
      const existingAccounts = await db.accounts
        .where('engagementId')
        .equals(engagement.id!)
        .toArray();

      // Normalize keys for robust matching (trim + lowercase)
      const existingAccountNumbers = new Map(
        existingAccounts.map(acc => [acc.accountNumber.trim().toLowerCase(), acc])
      );

      const conflictAccounts: ConflictAccount[] = [];

      for (const row of data) {
        // Normalize lookup key
        const existing = existingAccountNumbers.get(row.accountNumber.trim().toLowerCase());
        if (existing) {
          conflictAccounts.push({
            accountNumber: row.accountNumber,
            accountName: row.accountName,
            existingBalance: existing.bookBalance,
            newBalance: row.bookBalance
          });
        }
      }

      if (conflictAccounts.length > 0) {
        setConflicts(conflictAccounts);
        setStatus('conflicts');
      } else {
        await handleImport(data);
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Validation failed.');
      console.error('Validation error:', error);
    }
  };

  // Fix: accept optional resolutionOverride to bypass state update lag
  const handleImport = async (data: ParsedRow[], resolutionOverride?: 'replace') => {
    setStatus('importing');

    // Use override if provided (prevents race condition), otherwise fall back to state
    const effectiveResolution = resolutionOverride || conflictResolution;

    try {
      const now = Date.now();
      
      if (effectiveResolution === 'replace') {
        const existingAccounts = await db.accounts
          .where('engagementId')
          .equals(engagement.id!)
          .toArray();

        // Normalize keys to ensure matching works correctly
        const existingAccountMap = new Map<string, TBAccount>(
          existingAccounts.map(acc => [acc.accountNumber.trim().toLowerCase(), acc])
        );

        for (const row of data) {
          const normalizedKey = row.accountNumber.trim().toLowerCase();
          const existingAccount = existingAccountMap.get(normalizedKey);
          
          if (existingAccount && existingAccount.id) {
            // Update existing account
            const updatedAccount: TBAccount = {
              ...existingAccount,
              accountName: row.accountName,
              bookBalance: row.bookBalance,
              entity: selectedEntity,
              updatedAt: now
            };
            
            await db.accounts.put(updatedAccount);
            console.log(`Updated existing account: ${row.accountNumber} (ID: ${existingAccount.id})`);
          } else {
            // Create new account
            const newAccount: TBAccount = {
              id: crypto.randomUUID(),
              engagementId: engagement.id!,
              entity: selectedEntity,
              accountNumber: row.accountNumber,
              accountName: row.accountName,
              bookBalance: row.bookBalance,
              rje: 0,
              aje: 0,
              mAdj: 0,
              pyAdjBook: 0,
              pyMAdj: 0,
              pyFtax: 0,
              pyBalance: 0,
              grouping: DEFAULT_GROUPING,
              subGrouping: DEFAULT_SUBGROUPING,
              createdAt: now,
              updatedAt: now
            };
            
            await db.accounts.add(newAccount);
            console.log(`Added new account: ${row.accountNumber}`);
          }
        }
      } else {
        // No conflicts or normal add
        const accountsToAdd: TBAccount[] = data.map(row => ({
          id: crypto.randomUUID(),
          engagementId: engagement.id!,
          entity: selectedEntity,
          accountNumber: row.accountNumber,
          accountName: row.accountName,
          bookBalance: row.bookBalance,
          rje: 0,
          aje: 0,
          mAdj: 0,
          pyAdjBook: 0,
          pyMAdj: 0,
          pyFtax: 0,
          pyBalance: 0,
          grouping: DEFAULT_GROUPING,
          subGrouping: DEFAULT_SUBGROUPING,
          createdAt: now,
          updatedAt: now
        }));

        await db.accounts.bulkAdd(accountsToAdd);
      }

      setStatus('success');
      setSuccessMessage(`Successfully imported ${data.length} account${data.length !== 1 ? 's' : ''}.`);
      
      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Import failed.');
      console.error('Import error:', error);
    }
  };

  const handleConflictResolve = async (resolution: 'replace' | 'cancel') => {
    setConflictResolution(resolution);
    
    if (resolution === 'cancel') {
      setStatus('idle');
      setParsedData([]);
      setConflicts([]);
    } else {
      // Fix: Pass explicit resolution to avoid state update race condition
      await handleImport(parsedData, 'replace');
    }
  };

  const handleDownloadTemplate = () => {
    const template = 'Account Number,Account Name,Book Balance\n' +
      '1000,Cash,5000.00\n' +
      '2000,Accounts Receivable,10000.00\n' +
      '2100,Accounts Payable,-4000.00\n' +
      '2200,Accrued Expenses,-3000.00\n' +
      '2300,Deferred Revenue,-2000.00\n' +
      '3000,Common Stock,-5000.00\n' +
      '3100,Retained Earnings,-1000.00\n' +
      '4000,Service Revenue,-2000.00\n' +
      '5000,Cost of Goods Sold,1500.00\n' +
      '5100,Office Supplies Expense,500.00\n' +
      '5200,Rent Expense,1000.00\n' +
      '5300,Utilities Expense,500.00\n' +
      '5400,Depreciation Expense,500.00\n' +
      '5500,Interest Expense,500.00\n' +
      '5600,Miscellaneous Expense,500.00';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trial_balance_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen || !engagement.id) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40">
      <div className="glass-panel rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300">
        <div className="flex items-center justify-between p-6 border-b border-slate-300 dark:border-neutral-700">
          <h2 className="text-xl font-semibold text-taxtrail-text dark:text-taxtrail-dark-text">
            Import Trial Balance
          </h2>
          <button
            onClick={onClose}
            disabled={status === 'importing'}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-taxtrail-red dark:text-red-400" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 space-y-4">
            {entities.length > 1 && (
              <div>
                <label className="block text-sm font-medium mb-1 text-taxtrail-text dark:text-taxtrail-dark-text">
                  Entity
                </label>
                <select
                  value={selectedEntity}
                  onChange={(e) => setSelectedEntity(e.target.value)}
                  disabled={status === 'importing'}
                  className="w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-sage-600 dark:focus:ring-sage-500 bg-white dark:bg-neutral-700 text-taxtrail-text dark:text-taxtrail-dark-text border border-slate-300 dark:border-neutral-700 transition-colors duration-300"
                >
                  {entities.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
                </select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-taxtrail-text dark:text-taxtrail-dark-text">
                Import accounts with zero balance?
              </label>
              <button
                onClick={() => setIncludeZeroBalance(!includeZeroBalance)}
                disabled={status === 'importing'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
                  includeZeroBalance 
                    ? 'bg-sage-600 dark:bg-sage-700' 
                    : 'bg-gray-300 dark:bg-neutral-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    includeZeroBalance ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {status === 'error' && errorMessage && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
              <CircleAlert className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">Import Failed</p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">{errorMessage}</p>
              </div>
            </div>
          )}

          {status === 'success' && successMessage && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
              <CircleCheck className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Import Successful</p>
                <p className="text-sm text-green-700 dark:text-green-400 mt-1">{successMessage}</p>
              </div>
            </div>
          )}

          {status === 'conflicts' && conflicts.length > 0 && (
            <div className="mb-6">
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-3">
                  {conflicts.length} account{conflicts.length !== 1 ? 's' : ''} already exist{conflicts.length === 1 ? 's' : ''} in this engagement:
                </p>
                <div className="max-h-32 overflow-y-auto mb-4">
                  <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                    {conflicts.map((conflict, index) => (
                      <li key={index}>
                        {conflict.accountNumber} - {conflict.accountName}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleConflictResolve('replace')}
                    className="flex-1 px-4 py-2 bg-sage-600 hover:bg-sage-700 text-white rounded font-medium transition-colors"
                  >
                    Replace Existing
                  </button>
                  <button
                    onClick={() => handleConflictResolve('cancel')}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium transition-colors"
                  >
                    Cancel Import
                  </button>
                </div>
              </div>
            </div>
          )}

          {status === 'idle' && (
            <div>
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  isDragging
                    ? 'border-sage-600 bg-sage-50 dark:bg-sage-900/20'
                    : 'border-slate-300 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700'
                }`}
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-neutral-500" />
                <p className="text-sm font-medium text-taxtrail-text dark:text-taxtrail-dark-text mb-2">
                  Drag and drop your file here, or
                </p>
                <button
                  onClick={handleBrowseClick}
                  className="text-sm text-sage-600 dark:text-sage-500 hover:text-sage-700 dark:hover:text-sage-400 font-medium transition-colors"
                >
                  Browse Files
                </button>
                <p className="text-xs text-gray-500 dark:text-neutral-400 mt-3">
                  Supported formats: CSV, XLSX, XLS
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>

              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>
            </div>
          )}

          {(status === 'parsing' || status === 'validating' || status === 'importing') && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-600 border-t-sage-600 dark:border-t-sage-500 rounded-full animate-spin mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {status === 'parsing' && 'Parsing file...'}
                {status === 'validating' && 'Validating data...'}
                {status === 'importing' && 'Importing accounts...'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

